import React, { useRef, useState, Suspense, useCallback, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';
import VirtualJoystick from './VirtualJoystick';
import DragCamera from './DragCamera';
import './OfficeSceneControls.css';

/**
 * Cena 3D do Escritório Virtual
 * Renderiza o ambiente voxel com controles de joystick + drag
 */
const OfficeScene = ({ projectId, officeConfig, onReady, onError }) => {
  const [isLocked, setIsLocked] = useState(false);
  const cameraRotation = useRef({ x: 0, y: 0 });
  const joystickDirection = useRef({ x: 0, z: 0 });
  const isJumping = useRef(false);
  const jumpVelocity = useRef(0);

  // Constantes
  const JUMP_FORCE = 5;
  const GRAVITY = 9.8;

  // Callback do joystick
  const handleJoystickMove = useCallback((direction) => {
    joystickDirection.current = direction;
  }, []);

  // Callback da rotação da câmera
  const handleCameraRotate = useCallback((rotation) => {
    cameraRotation.current = rotation;
  }, []);

  // Iniciar sessão (clique para ativar)
  const handleStart = useCallback(() => {
    setIsLocked(true);
  }, []);

  // Sair
  const handleExit = useCallback(() => {
    setIsLocked(false);
  }, []);

  // Pular
  const handleJump = useCallback(() => {
    if (!isJumping.current) {
      isJumping.current = true;
      jumpVelocity.current = JUMP_FORCE;
    }
  }, []);

  return (
    <div className="office-scene-container">
      {/* Canvas 3D */}
      <Canvas
        shadows
        gl={{ antialias: true, alpha: false }}
        camera={{ fov: 75, near: 0.1, far: 1000 }}
        onCreated={() => onReady?.()}
      >
        {/* Background */}
        <color attach="background" args={['#1a1a2e']} />
        
        {/* Fog for atmosphere */}
        <fog attach="fog" args={['#1a1a2e', 20, 80]} />

        {/* Lighting */}
        <ambientLight intensity={0.4} />
        <directionalLight
          position={[10, 20, 10]}
          intensity={0.8}
          castShadow
          shadow-mapSize={[2048, 2048]}
        />
        <pointLight position={[0, 5, 0]} intensity={0.3} color="#6366f1" />

        {/* Camera and Controls */}
        <Suspense fallback={null}>
          <PlayerCamera
            isLocked={isLocked}
            cameraRotation={cameraRotation}
            joystickDirection={joystickDirection}
            isJumping={isJumping}
            jumpVelocity={jumpVelocity}
            floorSize={officeConfig?.office_layout?.floor_size}
          />
          <OfficeWorld officeConfig={officeConfig} />
        </Suspense>
      </Canvas>

      {/* Controles de UI */}
      {isLocked && (
        <>
          {/* Joystick Virtual (esquerda) */}
          <VirtualJoystick onMove={handleJoystickMove} />

          {/* Controle de Câmera por Drag (toda tela) */}
          <DragCamera onRotate={handleCameraRotate} isLocked={isLocked} />

          {/* Botão Pular (direita) */}
          <button 
            className="jump-button"
            onTouchStart={handleJump}
            onMouseDown={handleJump}
          >
            <span className="jump-icon">⬆</span>
            <span className="jump-label">Pular</span>
          </button>

          {/* Botão Sair */}
          <button className="exit-button" onClick={handleExit}>
            ✕
          </button>
        </>
      )}

      {/* Tela de início */}
      {!isLocked && (
        <div className="start-overlay" onClick={handleStart}>
          <div className="start-content">
            <h2>Escritório Virtual</h2>
            <p className="start-subtitle">Clique para entrar</p>
            <div className="start-controls">
              <div className="control-item">
                <span className="control-icon">🕹️</span>
                <span>Joystick para mover</span>
              </div>
              <div className="control-item">
                <span className="control-icon">👆</span>
                <span>Arraste para olhar</span>
              </div>
              <div className="control-item">
                <span className="control-icon">⬆</span>
                <span>Botão para pular</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Câmera do jogador controlada por joystick + drag
 */
const PlayerCamera = ({ isLocked, cameraRotation, joystickDirection, isJumping, jumpVelocity, floorSize }) => {
  const { camera } = useThree();
  const velocity = useRef(new THREE.Vector3());

  // Constantes
  const WALK_SPEED = 5;
  const EYE_HEIGHT = 1.6;
  const DAMPING = 0.85;
  const GRAVITY = 9.8;

  // Inicializar posição
  useEffect(() => {
    camera.position.set(5, EYE_HEIGHT, 5);
    camera.rotation.order = 'YXZ';
  }, [camera]);

  // Update loop
  useFrame((_, delta) => {
    if (!isLocked) return;

    // Aplicar rotação da câmera (do drag)
    camera.rotation.y = cameraRotation.current.y;
    camera.rotation.x = cameraRotation.current.x;

    // Movimento do joystick
    const moveX = joystickDirection.current.x;
    const moveZ = joystickDirection.current.z;

    // Calcular direção baseada na rotação da câmera
    const forward = new THREE.Vector3(0, 0, -1);
    const right = new THREE.Vector3(1, 0, 0);
    
    forward.applyQuaternion(camera.quaternion);
    forward.y = 0;
    forward.normalize();
    
    right.applyQuaternion(camera.quaternion);
    right.y = 0;
    right.normalize();

    // Aplicar movimento
    velocity.current.x = (right.x * moveX + forward.x * moveZ) * WALK_SPEED;
    velocity.current.z = (right.z * moveX + forward.z * moveZ) * WALK_SPEED;

    // Mover câmera
    camera.position.x += velocity.current.x * delta;
    camera.position.z += velocity.current.z * delta;

    // Damping
    velocity.current.x *= DAMPING;
    velocity.current.z *= DAMPING;

    // Pulo e gravidade
    if (isJumping.current) {
      camera.position.y += jumpVelocity.current * delta;
      jumpVelocity.current -= GRAVITY * delta;

      if (camera.position.y <= EYE_HEIGHT) {
        camera.position.y = EYE_HEIGHT;
        isJumping.current = false;
        jumpVelocity.current = 0;
      }
    } else {
      camera.position.y = EYE_HEIGHT;
    }

    // Boundary check
    const width = floorSize?.width || 24;
    const depth = floorSize?.depth || 21;
    const margin = 1;

    camera.position.x = Math.max(margin, Math.min(width - margin, camera.position.x));
    camera.position.z = Math.max(margin, Math.min(depth - margin, camera.position.z));
  });

  return <PerspectiveCamera makeDefault position={[5, 1.6, 5]} />;
};

/**
 * Mundo do escritório - Ambiente realista com divisórias de vidro
 */
const OfficeWorld = ({ officeConfig }) => {
  const rooms = officeConfig?.office_layout?.rooms || [];
  const floorSize = officeConfig?.office_layout?.floor_size || { width: 24, depth: 21 };

  return (
    <group>
      {/* Chão - Carpete cinza claro */}
      <Floor size={floorSize} />
      
      {/* Paredes externas - Branco */}
      <Walls size={floorSize} />
      
      {/* Divisórias internas com vidro */}
      <OfficeDividers rooms={rooms} />
      
      {/* Ponto de spawn */}
      <SpawnPoint />
      
      {/* Decoração */}
      <OfficeDecoration />
    </group>
  );
};

/**
 * Chão - Carpete cinza claro
 */
const Floor = ({ size }) => (
  <mesh rotation={[-Math.PI / 2, 0, 0]} position={[size.width / 2, 0, size.depth / 2]} receiveShadow>
    <planeGeometry args={[size.width, size.depth]} />
    <meshStandardMaterial 
      color="#e8e8e8" 
      roughness={0.9}
      metalness={0}
    />
  </mesh>
);

/**
 * Paredes externas - Branco com papel de parede sutil
 */
const Walls = ({ size }) => {
  const wallHeight = 3;
  const wallThickness = 0.15;

  return (
    <group>
      {/* Parede traseira */}
      <WallSection 
        position={[size.width / 2, wallHeight / 2, 0]} 
        size={[size.width, wallHeight, wallThickness]} 
      />
      
      {/* Parede frontal */}
      <WallSection 
        position={[size.width / 2, wallHeight / 2, size.depth]} 
        size={[size.width, wallHeight, wallThickness]} 
      />
      
      {/* Parede esquerda */}
      <WallSection 
        position={[0, wallHeight / 2, size.depth / 2]} 
        size={[wallThickness, wallHeight, size.depth]} 
      />
      
      {/* Parede direita */}
      <WallSection 
        position={[size.width, wallHeight / 2, size.depth / 2]} 
        size={[wallThickness, wallHeight, size.depth]} 
      />
    </group>
  );
};

/**
 * Seção de parede branca
 */
const WallSection = ({ position, size }) => (
  <mesh position={position} castShadow receiveShadow>
    <boxGeometry args={size} />
    <meshStandardMaterial 
      color="#ffffff" 
      roughness={0.5}
      metalness={0}
    />
  </mesh>
);

/**
 * Divisórias de escritório com vidro (metade vidro, metade parede)
 */
const OfficeDividers = ({ rooms }) => {
  // Configuração das divisórias baseada nas salas
  const dividers = [
    // Divisória entre Recepção e Área de Trabalho (horizontal)
    {
      position: { x: 5, z: 8 },
      size: { width: 10, height: 3 },
      rotation: 0,
      hasDoor: true,
      doorPosition: 0.3 // 30% da largura
    },
    // Divisória entre Área de Trabalho e Sala de Reunião (vertical)
    {
      position: { x: 15, z: 15 },
      size: { width: 10, height: 3 },
      rotation: Math.PI / 2,
      hasDoor: true,
      doorPosition: 0.5
    },
    // Divisória entre Copa e Sala de Reunião (horizontal)
    {
      position: { x: 19, z: 8 },
      size: { width: 8, height: 3 },
      rotation: 0,
      hasDoor: true,
      doorPosition: 0.7
    },
    // Divisória entre Recepção e Copa (vertical)
    {
      position: { x: 15, z: 4 },
      size: { width: 8, height: 3 },
      rotation: Math.PI / 2,
      hasDoor: false
    }
  ];

  return (
    <group>
      {dividers.map((divider, index) => (
        <GlassDivider 
          key={index} 
          {...divider} 
        />
      ))}
    </group>
  );
};

/**
 * Divisória com vidro (metade inferior parede, metade superior vidro)
 */
const GlassDivider = ({ position, size, rotation, hasDoor, doorPosition }) => {
  const wallHeight = 1.5; // Metade inferior
  const glassHeight = 1.5; // Metade superior
  const thickness = 0.08;
  const doorWidth = 1.0;
  const doorHeight = 2.2;

  // Calcular posição da porta
  const doorX = hasDoor ? (doorPosition - 0.5) * size.width : 0;

  return (
    <group 
      position={[position.x, 0, position.z]}
      rotation={[0, rotation, 0]}
    >
      {/* Parte inferior - Parede branca */}
      <mesh position={[0, wallHeight / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[size.width, wallHeight, thickness]} />
        <meshStandardMaterial color="#ffffff" roughness={0.5} />
      </mesh>

      {/* Parte superior - Vidro transparente */}
      <mesh position={[0, wallHeight + glassHeight / 2, 0]}>
        <boxGeometry args={[size.width, glassHeight, thickness * 0.5]} />
        <meshPhysicalMaterial 
          color="#a8d8ea"
          transparent
          opacity={0.3}
          roughness={0}
          metalness={0.1}
          transmission={0.9}
          thickness={0.5}
        />
      </mesh>

      {/* Moldura do vidro (alumínio) */}
      <mesh position={[0, wallHeight + glassHeight / 2, 0]}>
        <boxGeometry args={[size.width + 0.1, glassHeight + 0.1, thickness * 0.3]} />
        <meshStandardMaterial color="#c0c0c0" roughness={0.3} metalness={0.8} />
      </mesh>

      {/* Porta (se houver) */}
      {hasDoor && (
        <group position={[doorX, 0, 0]}>
          {/* Frame da porta */}
          <mesh position={[0, doorHeight / 2, 0]} castShadow receiveShadow>
            <boxGeometry args={[doorWidth + 0.1, doorHeight, thickness + 0.02]} />
            <meshStandardMaterial color="#f5f5f5" roughness={0.5} />
          </mesh>
          
          {/* Porta - parte inferior (madeira/mdf branco) */}
          <mesh position={[0, wallHeight / 2, 0]} castShadow receiveShadow>
            <boxGeometry args={[doorWidth - 0.05, wallHeight - 0.1, thickness]} />
            <meshStandardMaterial color="#ffffff" roughness={0.6} />
          </mesh>
          
          {/* Porta - parte superior (vidro) */}
          <mesh position={[0, wallHeight + (doorHeight - wallHeight) / 2, 0]}>
            <boxGeometry args={[doorWidth - 0.05, doorHeight - wallHeight - 0.1, thickness * 0.5]} />
            <meshPhysicalMaterial 
              color="#a8d8ea"
              transparent
              opacity={0.3}
              roughness={0}
              metalness={0.1}
              transmission={0.9}
            />
          </mesh>
          
          {/* Maçaneta */}
          <mesh position={[doorWidth / 2 - 0.15, 1, thickness / 2 + 0.05]}>
            <cylinderGeometry args={[0.03, 0.03, 0.15, 8]} />
            <meshStandardMaterial color="#c0c0c0" metalness={0.9} roughness={0.2} />
          </mesh>
        </group>
      )}
    </group>
  );
};

/**
 * Decoração do escritório
 */
const OfficeDecoration = () => {
  return (
    <group>
      {/* Plantas decorativas */}
      <Plant position={[2, 0, 2]} />
      <Plant position={[22, 0, 2]} />
      <Plant position={[22, 0, 19]} />
      
      {/* Quadro na parede */}
      <WallArt position={[12, 1.8, 0.1]} />
    </group>
  );
};

/**
 * Planta decorativa
 */
const Plant = ({ position }) => (
  <group position={position}>
    {/* Vaso */}
    <mesh position={[0, 0.3, 0]} castShadow>
      <cylinderGeometry args={[0.25, 0.3, 0.6, 8]} />
      <meshStandardMaterial color="#8b7355" roughness={0.8} />
    </mesh>
    
    {/* Planta (esferas verdes) */}
    <mesh position={[0, 1, 0]}>
      <sphereGeometry args={[0.4, 8, 8]} />
      <meshStandardMaterial color="#228b22" roughness={0.8} />
    </mesh>
    <mesh position={[0.2, 1.3, 0.1]}>
      <sphereGeometry args={[0.3, 8, 8]} />
      <meshStandardMaterial color="#228b22" roughness={0.8} />
    </mesh>
    <mesh position={[-0.15, 1.2, -0.1]}>
      <sphereGeometry args={[0.25, 8, 8]} />
      <meshStandardMaterial color="#228b22" roughness={0.8} />
    </mesh>
  </group>
);

/**
 * Quadro na parede
 */
const WallArt = ({ position }) => (
  <group position={position}>
    {/* Moldura */}
    <mesh>
      <boxGeometry args={[2, 1.2, 0.05]} />
      <meshStandardMaterial color="#2c2c2c" roughness={0.3} />
    </mesh>
    
    {/* Tela/Quadro */}
    <mesh position={[0, 0, 0.03]}>
      <planeGeometry args={[1.8, 1]} />
      <meshStandardMaterial color="#6366f1" roughness={0.5} />
    </mesh>
  </group>
);

const SpawnPoint = () => {
  const meshRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.02;
      meshRef.current.position.y = 0.5 + Math.sin(state.clock.elapsedTime * 2) * 0.1;
    }
  });

  return (
    <group position={[5, 0, 5]}>
      <mesh ref={meshRef}>
        <octahedronGeometry args={[0.3]} />
        <meshStandardMaterial color="#6366f1" emissive="#6366f1" emissiveIntensity={0.5} />
      </mesh>
      <pointLight color="#6366f1" intensity={1} distance={5} />
    </group>
  );
};

export default OfficeScene;
